import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { isEmbedded } from '@/lib/embed'
import type { UserRole, OrgPlan } from '@/api/types'

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  expiresAt: string | null
  userId: string | null
  orgId: string | null
  orgName: string | null
  orgPlan: OrgPlan | null
  userEmail: string | null
  role: UserRole | null
  isAuthenticated: boolean
  // Set when a session was dropped because its role may not use the portal; shown on /login.
  accessDenied: boolean

  setSession: (params: {
    accessToken: string
    refreshToken: string
    expiresAt: string
    userId: string
    orgId: string
    role: UserRole
  }) => void
  setOrgInfo: (name: string, plan: OrgPlan) => void
  setUserEmail: (email: string) => void
  clearSession: () => void
  denyAccess: () => void
  updateTokens: (accessToken: string, refreshToken: string, expiresAt: string) => void
  // Embed mode: session handed over by the partner parent (access token only, no refresh token).
  applyAccessToken: (accessToken: string, expiresAt: string) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      userId: null,
      orgId: null,
      orgName: null,
      orgPlan: null,
      userEmail: null,
      role: null,
      isAuthenticated: false,
      accessDenied: false,

      setSession: ({ accessToken, refreshToken, expiresAt, userId, orgId, role }) =>
        set({ accessToken, refreshToken, expiresAt, userId, orgId, orgName: null, orgPlan: null, userEmail: null, role, isAuthenticated: true, accessDenied: false }),

      setOrgInfo: (name, plan) => set({ orgName: name, orgPlan: plan }),

      setUserEmail: (email) => set({ userEmail: email }),

      clearSession: () =>
        set({
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          userId: null,
          orgId: null,
          orgName: null,
          orgPlan: null,
          userEmail: null,
          role: null,
          isAuthenticated: false,
          accessDenied: false,
        }),

      denyAccess: () => {
        useAuthStore.getState().clearSession()
        set({ accessDenied: true })
      },

      updateTokens: (accessToken, refreshToken, expiresAt) =>
        set({ accessToken, refreshToken, expiresAt }),

      applyAccessToken: (accessToken, expiresAt) => {
        const claims = parseJwtClaims(accessToken)
        set((s) => {
          // Different user or org → drop cached profile so Layout refetches it.
          const sameUser = s.userId === claims.uid && s.orgId === claims.oid
          return {
            accessToken,
            refreshToken: null,
            expiresAt,
            userId: claims.uid,
            orgId: claims.oid,
            role: claims.role ?? null,
            isAuthenticated: true,
            ...(sameUser ? {} : { orgName: null, orgPlan: null, userEmail: null }),
          }
        })
      },
    }),
    {
      name: 'qtz-auth-storage',
      partialize: ({ accessDenied: _accessDenied, ...rest }) => rest,
      // Embedded sessions are per-tab and must never mix with a top-level login.
      storage: createJSONStorage(() => (isEmbedded() ? sessionStorage : localStorage)),
    }
  )
)

// ── JWT claim extraction ───────────────────────────────────────────────────────

interface JwtClaims {
  uid: string
  oid: string
  role: UserRole
}

export function parseJwtClaims(token: string): JwtClaims {
  const payload = token.split('.')[1]
  return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as JwtClaims
}
